import React, {useCallback} from 'react';
import {Link, useNavigate} from 'react-router-dom';

import {useOktaAuth} from '@okta/okta-react';
import {getUser} from 'src/modules/admin/services';
import {clearAttributesFilters} from 'src/modules/attributes/services/filters/actions';
import {clearChangeRequestsFilters} from 'src/modules/change-requests/services/filters/actions';
import {clearEventsFilters} from 'src/modules/events/services/filters/actions';
import {Role} from 'src/openapi/models/Role';
import {useAppDispatch, useAppSelector} from 'src/store';

import {AvatarWrapped} from '@pb/kit-component-avatar';
import {FlatButton} from '@pb/kit-component-button';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons, Sizes} from '@pb/kit-component-icon';
import {PageLogo} from '@pb/kit-component-page-v2';
import {LocalStorage} from '@pb/kit-util-client-storage';

import {pageLogoLink} from './nav-bar.module.styl';

const CONFLUENCE_LINK = 'https://pitchbook.atlassian.net/wiki/spaces/UD/pages/304120005/Event+Tracking';

export const NavBar = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {oktaAuth} = useOktaAuth();
    const user = useAppSelector(getUser) || LocalStorage.get('user');
    const userRoles = user?.roles || [];
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';
    const avatar = user?.avatar || '';

    const onClickDocumentation = useCallback(() => {
        window.open(CONFLUENCE_LINK);
    }, []);

    const onLogout = () => {
        if (oktaAuth.signOut) {
            oktaAuth.signOut();
        }
    };

    const onLogoClick = () => {
        dispatch(clearEventsFilters());
        dispatch(clearAttributesFilters());
        dispatch(clearChangeRequestsFilters());
    };

    return (
        <Group justify="space-between">
            <Group direction="v" gap="none">
                <Link to="/" className={pageLogoLink} onClick={onLogoClick}>
                    <PageLogo className="pl-xl"/>
                </Link>
                <Item className="pl-xl">
                    <strong>UD Event Design Tool</strong>
                </Item>
            </Group>
            <Group alignItems="center">
                <FlatButton
                    startIcon={<Icon type={Icons.QUESTION_CIRCLE} size={Sizes.Size_16}/>}
                    onClick={onClickDocumentation}
                    variant="secondary">
                    Documentation
                </FlatButton>
                <DropdownMenu
                    anchor={({getAnchorProps, anchorRef}) => (
                        <FlatButton
                            {...getAnchorProps()}
                            ref={anchorRef}
                            startIcon={(
                                <Item className="pr-s">
                                    <AvatarWrapped
                                        alt={`${firstName} ${lastName}`}
                                        name={{firstName, lastName}}
                                        src={avatar}
                                        placeholder={Icons.PERSON_ROUNDED}
                                    />
                                </Item>
                            )}
                            endIcon={(<Icon type={Icons.ANGLE_DOWN}/>)}
                            variant="secondary">
                            {`${firstName} ${lastName}`}
                        </FlatButton>
                    )}>
                    <Item style={{width: '15rem'}}>
                        {userRoles.includes(Role.ROLE_UDEDT_ADMIN) && (
                            <DropdownMenuItem caption="Admin" onClick={() => navigate('/admin')}/>
                        )}
                        <DropdownMenuItem caption="User Settings" onClick={() => navigate('/user-settings')}/>
                        <DropdownMenuItem caption="Logout" separated={true} onClick={onLogout}/>
                    </Item>
                </DropdownMenu>
            </Group>
        </Group>
    );
};
